/**
 * Waitlist — standalone page, no V2Layout / sidebar.
 *
 * v2 (2026-05-18): redesigned as a full marketing landing page in light theme.
 * Matt-approved design currently previewed at /design/preview-landing-v2-x9k7/.
 *
 * Sections, top-to-bottom (matches clear-graphics rules 1–7):
 *   1. Header — bare wordmark + sign-in link + single CTA
 *   2. Hero — headline + lede + CTA + product screenshot + founder quote
 *   3. Proof bar — research-grade benchmarks
 *   4. The problem — VLMs hallucinate, JEPA can't
 *   5. How it works — one brain, many products + 3 features
 *   6. Determinism — same frame, same prompt, same answer
 *   7. JEPA architecture
 *   8. Founders — Matt + Mehdi
 *   9. Pricing teaser
 *   10. Final waitlist form (page ends with action)
 *   11. Footer
 *
 * Form fields map to the existing submitWaitlist API:
 *   email      → email
 *   full_name  → name
 *   company    → company
 *   vertical   → industry select (use_case alias for compatibility)
 *
 * Success state preserves the existing "You're #N in the queue" + "what's next"
 * messaging but restyled to match the v2 design language.
 */

import { useState, type FormEvent } from 'react'
import { submitWaitlist } from '../lib/api'
import { trackWaitlistSubmit, trackCareersMailtoClicked } from '../lib/analytics'

type Status = 'idle' | 'loading' | 'success' | 'error'
type WaitlistResult = { position: number; alreadyOnList?: boolean }

// Assets live alongside the preview HTML so both routes share them.
const ASSETS = '/design/preview-landing-v2-x9k7/assets'

export default function Waitlist() {
  const [status, setStatus]     = useState<Status>('idle')
  const [errorMsg, setErrorMsg] = useState('')
  const [result, setResult]     = useState<WaitlistResult | null>(null)

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setStatus('loading')
    setErrorMsg('')
    const form = e.currentTarget
    const verticalValue = (form.elements.namedItem('vertical') as HTMLSelectElement).value
    const data = {
      email:     (form.elements.namedItem('email')   as HTMLInputElement).value,
      full_name: (form.elements.namedItem('name')    as HTMLInputElement).value,
      company:   (form.elements.namedItem('company') as HTMLInputElement).value,
      use_case:  verticalValue,
      vertical:  verticalValue,
    }
    try {
      const res = await submitWaitlist(data) as WaitlistResult & { id?: string; email?: string; status?: string; position?: number }
      trackWaitlistSubmit(true)
      setResult({ position: res.position ?? 1 })
      setStatus('success')
      // Bring the confirmation card into view. It renders at the BOTTOM of the
      // page (replacing the form), so scrolling to the top of the document hides
      // it — which is exactly the regression Matt hit on mobile (got the email,
      // saw no on-page confirmation). Scroll the card itself into view instead.
      scrollSuccessIntoView()
    } catch (err) {
      // A 409 "already_on_waitlist" is not a failure from the user's point of
      // view — they ARE on the list. Show the confirmation, not a red error.
      const code = (err as { code?: string })?.code
      const status = (err as { status?: number })?.status
      if (code === 'already_on_waitlist' || status === 409) {
        trackWaitlistSubmit(true)
        setResult({ position: 0, alreadyOnList: true })
        setStatus('success')
        scrollSuccessIntoView()
        return
      }
      trackWaitlistSubmit(false)
      setStatus('error')
      setErrorMsg(err instanceof Error ? err.message : 'Something went wrong. Please try again.')
    }
  }

  return (
    <>
      <style>{pageStyles}</style>
      <div className="lv2">

        {/* PRI-503: free-trial banner — the waitlist is retired; this page
            stays live for email links + the form (POST /v1/waitlist is probed
            by a monitor cron), but visitors should head to the free trial. */}
        <div
          data-testid="waitlist-trial-banner"
          style={{
            background: '#1B4FE0', color: '#fff', textAlign: 'center',
            padding: '10px 16px', fontSize: 14, lineHeight: 1.5,
          }}
        >
          No waitlist anymore — you can try Primate Intelligence right now, free, no card required.{' '}
          <a href="/" style={{ color: '#fff', fontWeight: 600, textDecoration: 'underline' }}>
            Try it for free →
          </a>
        </div>

        {/* ── HEADER ───────────────────────────────────────────── */}
        <header className="header">
          <div className="header-inner">
            <a className="brand" href="/" aria-label="Primate Intelligence — home">
              <img src={`${ASSETS}/Darwin_v3_vinyl_transparent.png`} alt="Darwin" />
              <div className="wordmark">Primate<span> Intelligence</span></div>
            </a>
            <div className="header-right">
              <a className="signin" href="/login" data-testid="waitlist-header-login-link"><span className="signin-prefix">Already invited? </span><strong>Sign in →</strong></a>
              <a className="cta" href="#waitlist">Request early access</a>
            </div>
          </div>
        </header>

        {/* ── 1. HERO ──────────────────────────────────────────── */}
        <section className="hero">
          <div className="container hero-inner">
            <div className="hero-text">
              <h1 className="h1">Darwin tells you what's happening in real time.</h1>
              <p className="lede">Real-time video scene understanding of Actions and Objects at a fraction of the cost of VLMs.</p>
              <div className="hero-actions">
                <a className="cta cta-lg" href="#waitlist">Request early access</a>
                <a className="cta-link" href="#proof">See the proof →</a>
              </div>
              <figure className="darwin-quote">
                <img src={`${ASSETS}/slide-hero-darwin-watching.jpg`} alt="Darwin — the JEPA world model" />
                <figcaption>
                  <p>&ldquo;Detecting actions from video without hallucinations, in real-time is a game changer&rdquo;</p>
                  <cite>Matt Miesnieks · CEO &amp; Co-founder</cite>
                </figcaption>
              </figure>
            </div>
            <figure className="product-shot product-shot-hero">
              <img
                src={`${ASSETS}/product-screenshot.png`}
                alt="Primate Vision — natural language compiled into a structured ACTION query, narrative results streaming in real time"
              />
            </figure>
          </div>
        </section>

        {/* ── 2. PROOF BAR ─────────────────────────────────────── */}
        <section className="proof" id="proof">
          <div className="container proof-inner">
            <div className="proof-label">Research-grade JEPA performance.<br />Production API. Available now.</div>
            <div className="proof-tile"><div className="num">84.2<span className="pct">%</span></div><div className="lbl">SSv2 accuracy</div></div>
            <div className="proof-tile"><div className="num">44.1</div><div className="lbl">EK-100 recall@5</div></div>
            <div className="proof-tile"><div className="num">11<span className="pct">ms</span></div><div className="lbl">P50 latency</div></div>
          </div>
        </section>

        {/* ── 3. THE PROBLEM ────────────────────────────────────── */}
        <section className="block">
          <div className="container block-grid">
            <div>
              <h2 className="h2">VLMs hallucinate.<br />JEPA can't.</h2>
              <p className="lede">Ask the same question three times. Get three different answers. Primate Vision is deterministic — it reasons from the video, not pattern completion.</p>
              <p className="lede">Visual-language models are pattern completers. They're confident, even when they're wrong. And they're expensive — roughly $120/camera/month to run in real-time.</p>
              <div style={{ marginTop: 28 }}><a className="cta" href="#waitlist">Request early access</a></div>
            </div>
            <div className="slide-card slide-card-dark-img" style={{ aspectRatio: '4 / 3' }}>
              <div className="img-wrap" style={{ width: '100%', height: '100%' }}>
                <img src={`${ASSETS}/slide2-jepa-cant.png`} alt="VLMs hallucinate. JEPA can't." />
              </div>
            </div>
          </div>
        </section>

        {/* ── 4. HOW IT WORKS ─────────────────────────────────── */}
        <section className="block alt">
          <div className="container">
            <h2 className="h2">One JEPA brain.<br />Many sensory products.</h2>
            <p className="lede"><strong>Primate Brain</strong> is a JEPA world model — it builds memory, understands relationships and predicts actions. <strong>Primate Vision</strong> is the first perception modality attached to it.</p>

            <div className="features">
              <div className="feature">
                <div className="num">01 · Compile</div>
                <h3>Natural language compiles to structured JSON.</h3>
                <p>You speak English. Darwin speaks JSON. We parse using a 3rd party LLM for humans, not the model. Your application submits &amp; receives the same JSON structured output every time — counts, actions, attributes, locations — never prose that needs re-parsing.</p>
                <div className="chips">
                  <span className="chip">Subject</span><span className="chip">Action</span><span className="chip">Presence</span>
                  <span className="chip">Count</span><span className="chip">Threshold</span><span className="chip">Attribute</span>
                  <span className="chip">Location</span><span className="chip">State</span>
                </div>
              </div>

              <div className="feature">
                <div className="num">02 · Ingest</div>
                <h3>Webcam or file — same model.</h3>
                <p>Test on your laptop or phone with a webcam. Or upload a clip. Point it at your existing CCTV or Broadcast stream. Darwin's a model, not a pipeline — same output regardless of where the pixels came from.</p>
              </div>

              <div className="feature">
                <div className="num">03 · Run</div>
                <h3>30 fps on edge hardware.</h3>
                <p>Runs in real-time on Jetson and Mac hardware. Same prompt → same output, every time.</p>
              </div>
            </div>
          </div>
        </section>

        {/* ── 5. DETERMINISM ───────────────────────────────────── */}
        <section className="block">
          <div className="container block-grid reverse">
            <div>
              <h2 className="h2">Same frame.<br />Same prompt.<br />Same answer.</h2>
              <p className="lede">VLMs give you different answers to the same question because they're sampling from a language distribution. Darwin's predictive architecture observes and reports directly from the concepts in the video.</p>
              <p className="muted" style={{ fontStyle: 'italic', marginTop: 18, fontSize: 15 }}>"VLMs invent. JEPA observes."</p>
              <div style={{ marginTop: 28 }}><a className="cta" href="#waitlist">Request early access</a></div>
            </div>
            <div className="slide-card slide-card-dark-img" style={{ aspectRatio: '4 / 3' }}>
              <div className="img-wrap" style={{ width: '100%', height: '100%' }}>
                <img src={`${ASSETS}/slide3-10x-cheaper.png`} alt="10x cheaper. More accurate. Deterministic." />
              </div>
            </div>
          </div>
        </section>

        {/* ── 6. JEPA ARCHITECTURE ─────────────────────────────── */}
        <section className="block alt">
          <div className="container block-grid">
            <div>
              <h2 className="h2">It's not a VLM. It's a JEPA predictive world model.</h2>
              <p className="lede">Primate Intelligence is the first commercial deployment of a JEPA video world model for perception.</p>
              <p className="lede">The model builds an internal scene representation, predicts what it should observe, and reports the delta. No tokens, no captions, no hallucinating the answer.</p>
              {/* PRI-310: "Technical deep dive" link removed during friendly-tester phase. */}
              {/* Replaced with link to JEPA blog post (PRI-10 placeholder) when the public docs return, point this back at /docs. */}
              <div style={{ marginTop: 28 }}><a className="cta-link" href="https://www.primateintelligence.ai/blog">Read what we&rsquo;re working on →</a></div>
            </div>
            <div className="slide-card" style={{ aspectRatio: '4 / 3' }}>
              <img
                src={`${ASSETS}/slide0-brain-diagram.png`}
                alt="JEPA predictive world model — monkey brain diagram"
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              />
            </div>
          </div>
        </section>

        {/* ── 7. FOUNDERS ──────────────────────────────────────── */}
        <section className="block">
          <div className="container">
            <h2 className="h2">World-class CV / world-model team.</h2>
            <p className="lede">Matt founded 6D.ai (acquired by Niantic, 2020). Mehdi led AI development at 6D. Decades spent building systems that attempt to understand reality. Primate Intelligence is their bet on perception that leads to general intelligence.</p>

            <div className="founders">
              <div className="founder">
                <img src={`${ASSETS}/headshot-matt.jpg`} alt="Matt Miesnieks" />
                <div>
                  <h4>Matt Miesnieks</h4>
                  <div className="role">CEO &amp; Co-founder</div>
                  <p>Founder of 6D.ai (acq. Niantic, 2020). Serial entrepreneur, former VP Enterprise Sales &amp; Engineer</p>
                </div>
              </div>
              <div className="founder">
                <img src={`${ASSETS}/headshot-mehdi.jpg`} alt="Mehdi Nikkhah" />
                <div>
                  <h4>Mehdi Nikkhah</h4>
                  <div className="role">CTO &amp; Co-founder</div>
                  <p>AI PhD; led all CV engineering and research at 6D.ai. 15 years building world-class AI teams.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── 8. PRICING TEASER ───────────────────────────────── */}
        <section className="block alt">
          <div className="container">
            <h2 className="h2">Disruptive pricing resulting from disruptive technology</h2>
            <p className="lede" style={{ marginLeft: 'auto', marginRight: 'auto' }}>
              Simple prepaid credits — pay only for what you process. No subscriptions.
            </p>
            <div style={{ marginTop: 28 }}><a className="cta-link" href="https://www.primateintelligence.ai/pricing">See full pricing →</a></div>
          </div>
        </section>

        {/* ── 9. FINAL — WAITLIST FORM (or success) ───────────── */}
        <section className="final" id="waitlist">
          <div className="container">
            {status === 'success' ? (
              <SuccessCard position={result?.position ?? 1} alreadyOnList={result?.alreadyOnList} />
            ) : (
              <>
                <h2 className="h2" style={{ margin: '0 auto' }}>Get on the waitlist.</h2>
                <p className="lede" style={{ marginLeft: 'auto', marginRight: 'auto' }}>
                  We're inviting potential design-partners first.
                </p>
                <form className="final-form" onSubmit={handleSubmit} noValidate>
                  <input
                    id="email" name="email" type="email" required
                    placeholder="Email"
                    autoComplete="email"
                  />
                  <input
                    id="name" name="name" type="text" required
                    placeholder="Full name"
                    autoComplete="name"
                  />
                  <input
                    id="company" name="company" type="text" className="full"
                    placeholder="Company"
                    autoComplete="organization"
                  />
                  <select id="vertical" name="vertical" required defaultValue="" className="full">
                    <option value="" disabled>Industry…</option>
                    <option value="smart-cameras">Smart Cameras / Physical Security</option>
                    <option value="robotics">Robotics</option>
                    <option value="drones-uav">Drones / UAV</option>
                    <option value="autonomous-vehicles">Autonomous Vehicles</option>
                    <option value="vss">Video Search &amp; Summarization</option>
                    <option value="video-ad-tech">Video Advertising / Ad Tech</option>
                    <option value="healthcare">Healthcare / Medical Imaging</option>
                    <option value="research">Research / Academia</option>
                    <option value="other">Other / Custom</option>
                  </select>

                  {status === 'error' && (
                    <p className="form-error full" role="alert">{errorMsg}</p>
                  )}

                  <button
                    type="submit"
                    className="cta full"
                    disabled={status === 'loading'}
                  >
                    {status === 'loading' ? 'Submitting…' : 'Request early access'}
                  </button>
                </form>
                <p className="final-signin">
                  Already invited?{' '}
                  <a href="/login" data-testid="waitlist-login-link"><strong>Sign in →</strong></a>
                </p>
              </>
            )}
          </div>
        </section>

        {/* ── FOOTER ───────────────────────────────────────────── */}
        <footer>
          <div className="container footer-inner">
            <div>© 2026 Primate AI, Inc.</div>
            <nav>
              <a href="/privacy">Privacy</a>
              <a href="/terms">Terms</a>
              <a href="https://twitter.com/primateintel">@primateintel</a>
              <a
                href="mailto:careers@primateintelligence.ai"
                onClick={() => trackCareersMailtoClicked({
                  source: 'waitlist_page',
                  viewport: typeof window !== 'undefined' && window.innerWidth < 768 ? 'mobile' : 'desktop',
                })}
              >careers@</a>
            </nav>
          </div>
        </footer>

      </div>
    </>
  )
}

// Scroll the success card into the viewport after a submit. The card renders at
// the bottom of a long landing page; without this the user (especially on
// mobile) stays parked on the hero and sees no confirmation. Falls back to the
// #waitlist anchor if the card ref hasn't mounted yet.
function scrollSuccessIntoView() {
  if (typeof window === 'undefined') return
  // Defer to the next frame so the success card has rendered.
  requestAnimationFrame(() => {
    const el =
      document.querySelector('[data-testid="waitlist-success"]') ??
      document.getElementById('waitlist')
    try {
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    } catch {
      try { el?.scrollIntoView() } catch { /* noop */ }
    }
  })
}

// ── Success card ─────────────────────────────────────────────────────────────
function SuccessCard({ position, alreadyOnList }: { position: number; alreadyOnList?: boolean }) {
  const heading = alreadyOnList ? "You're already on the list." : "You're on the list."
  return (
    <div className="success-card" data-testid="waitlist-success">
      <div className="success-check" aria-hidden="true">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#15a957"
          strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="20 6 9 17 4 12" />
        </svg>
      </div>
      <h2 className="h2" style={{ margin: '0 auto', fontSize: 32 }}>{heading}</h2>
      {!alreadyOnList && (
      <div className="position-badge">
        <span className="position-num">#{position}</span>
        <span className="position-lbl">in the queue</span>
      </div>
      )}

      <div className="next-steps">
        <p className="next-steps-label">What happens next</p>
        <ol>
          <li><span className="step-num">01</span><span>Matt reviews your use case personally — usually within 24 hours.</span></li>
          <li><span className="step-num">02</span><span>You'll receive credentials and onboarding docs.</span></li>
          <li><span className="step-num">03</span><span>We'll schedule a 10-minute call to understand your use case.</span></li>
        </ol>
      </div>

      <a className="cta-link" href="/">← Back to home</a>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Styles — scoped under .lv2 so they don't leak into other pages.
// Mirrors the v2 static preview at /design/preview-landing-v2-x9k7/index.html
// ─────────────────────────────────────────────────────────────────────────────
const pageStyles = `
/* Light-mode tokens (carousel-light palette) */
.lv2 {
  --bg:           #FFFFFF;
  --bg-soft:      #F6F7F9;
  --bg-card:      #FFFFFF;
  --border:       #E4E6EB;
  --border-soft:  #EEF0F4;
  --text:         #0E1117;
  --text-muted:   #4A5160;
  --text-faint:   #7A8194;
  --signal:       #2058F0;
  --signal-soft:  rgba(32,88,240,0.08);
  --signal-line:  rgba(32,88,240,0.22);
  --green:        #15a957;
  --red:          #DC2626;
  --ink-dark:     #0A0B10;

  background: var(--bg);
  color: var(--text);
  font-family: 'Inter', system-ui, -apple-system, sans-serif;
  font-weight: 400;
  line-height: 1.6;
  -webkit-font-smoothing: antialiased;
}
.lv2 * { box-sizing: border-box; }
.lv2 ::selection { background: var(--signal); color: #fff; }
.lv2 a { color: var(--signal); text-decoration: none; }
.lv2 img { display: block; max-width: 100%; }

.lv2 .container { max-width: 1180px; margin: 0 auto; padding: 0 32px; }

/* Headings */
.lv2 .h1 {
  font-size: clamp(34px, 5.5vw, 64px);
  font-weight: 800;
  letter-spacing: -0.03em;
  line-height: 1.04;
  color: var(--text);
  max-width: 900px;
}
.lv2 .h2 {
  font-size: clamp(28px, 3.4vw, 44px);
  font-weight: 800;
  letter-spacing: -0.025em;
  line-height: 1.08;
  color: var(--text);
  max-width: 760px;
}
.lv2 .lede {
  margin-top: 18px;
  font-size: clamp(16px, 1.5vw, 18px);
  line-height: 1.6;
  color: var(--text-muted);
  max-width: 620px;
}
.lv2 .muted { color: var(--text-muted); }

/* CTA */
.lv2 .cta {
  display: inline-flex; align-items: center; justify-content: center; gap: 8px;
  background: var(--signal);
  color: #fff;
  border: none;
  padding: 14px 24px;
  font-family: inherit;
  font-size: 15px;
  font-weight: 600;
  letter-spacing: -0.005em;
  cursor: pointer;
  border-radius: 4px;
  transition: background 0.15s ease, transform 0.15s ease;
  text-decoration: none;
}
.lv2 .cta:hover:not(:disabled) { background: #1B4FE0; transform: translateY(-1px); }
.lv2 .cta:disabled { opacity: 0.6; cursor: progress; transform: none; }
.lv2 .cta.cta-lg { padding: 16px 28px; font-size: 15.5px; }
.lv2 .cta-link {
  display: inline-flex; align-items: center; gap: 6px;
  font-family: 'JetBrains Mono', ui-monospace, SFMono-Regular, monospace;
  font-size: 12px; font-weight: 700;
  letter-spacing: 0.08em; text-transform: uppercase;
  color: var(--signal);
}

/* Header */
.lv2 .header {
  position: sticky; top: 0; z-index: 50;
  background: rgba(255,255,255,0.92);
  backdrop-filter: blur(14px);
  border-bottom: 1px solid var(--border);
}
.lv2 .header-inner {
  display: flex; align-items: center; justify-content: space-between;
  height: 64px;
  max-width: 1180px;
  margin: 0 auto;
  padding: 0 32px;
}
.lv2 .brand { display: flex; align-items: center; gap: 12px; color: var(--text); }
.lv2 .brand img { height: 36px; }
.lv2 .brand .wordmark { font-size: 17px; font-weight: 700; letter-spacing: -0.01em; color: var(--text); }
.lv2 .brand .wordmark span { color: var(--signal); }
.lv2 .header-right { display: flex; align-items: center; gap: 18px; }
.lv2 .signin { font-size: 13px; color: var(--text-faint); font-weight: 500; }
.lv2 .signin strong { color: var(--signal); }

/* Hero */
.lv2 .hero { padding: 80px 0 64px; border-bottom: 1px solid var(--border-soft); }
.lv2 .hero-inner { display: grid; grid-template-columns: 0.8fr 1.2fr; gap: 56px; align-items: center; }
@media (max-width: 880px) { .lv2 .hero-inner { grid-template-columns: 1fr; gap: 32px; } }
.lv2 .hero-actions { display: flex; align-items: center; gap: 18px; margin-top: 32px; flex-wrap: wrap; }

.lv2 .product-shot {
  border: 1px solid var(--border);
  border-radius: 10px;
  overflow: hidden;
  background: var(--bg-soft);
  box-shadow: 0 16px 40px rgba(0,0,0,0.06), 0 6px 16px rgba(0,0,0,0.04);
  margin: 0;
}
.lv2 .product-shot img { width: 100%; height: auto; display: block; }

/* Hero — small Darwin image with founder quote */
.lv2 .darwin-quote {
  margin-top: 32px;
  padding-top: 22px;
  border-top: 1px solid var(--border);
  display: flex; align-items: center; gap: 16px;
  max-width: 520px;
}
.lv2 .darwin-quote img {
  width: 72px; height: 72px;
  border-radius: 50%;
  object-fit: cover;
  background: #F6F4EE;
  border: 1px solid var(--border);
  flex-shrink: 0;
}
.lv2 .darwin-quote figcaption { flex: 1; }
.lv2 .darwin-quote figcaption p {
  font-size: 14px; font-style: italic; color: var(--text-muted);
  line-height: 1.55; margin: 0;
}
.lv2 .darwin-quote figcaption cite {
  display: block; margin-top: 8px;
  font-family: 'JetBrains Mono', ui-monospace, SFMono-Regular, monospace;
  font-size: 10px; font-style: normal;
  letter-spacing: 0.08em; text-transform: uppercase;
  color: var(--text-faint);
}
@media (max-width: 600px) {
  .lv2 .darwin-quote { gap: 14px; }
  .lv2 .darwin-quote img { width: 60px; height: 60px; }
}

/* Slide cards (alternate sections) */
.lv2 .slide-card {
  position: relative;
  border-radius: 8px;
  border: 1px solid var(--border);
  background: var(--bg-card);
  box-shadow:
    0 24px 60px rgba(0,0,0,0.06),
    0 12px 32px rgba(0,0,0,0.04),
    0 6px 16px rgba(0,0,0,0.03);
  overflow: hidden;
}
.lv2 .slide-card::before {
  content: ''; position: absolute; left: 8px; right: 8px; top: 0;
  height: 2px; background: var(--signal-line);
  border-radius: 0 0 2px 2px;
}
.lv2 .slide-card-dark-img { background: var(--bg-card); }
.lv2 .slide-card-dark-img .img-wrap {
  background: var(--ink-dark);
  position: relative;
  overflow: hidden;
}
.lv2 .slide-card-dark-img .img-wrap img {
  width: 100%; height: 100%; object-fit: cover; display: block;
}

/* Proof bar */
.lv2 .proof {
  padding: 32px 0;
  background: var(--bg-soft);
  border-bottom: 1px solid var(--border-soft);
}
.lv2 .proof-inner {
  display: grid; grid-template-columns: 1.2fr repeat(3, 1fr); gap: 32px;
  align-items: center;
}
@media (max-width: 880px) { .lv2 .proof-inner { grid-template-columns: 1fr 1fr; } }
.lv2 .proof-label {
  font-family: 'JetBrains Mono', ui-monospace, SFMono-Regular, monospace;
  font-size: 11px; font-weight: 700;
  letter-spacing: 0.14em; text-transform: uppercase;
  color: var(--text-faint);
}
.lv2 .proof-tile .num {
  font-family: 'JetBrains Mono', ui-monospace, SFMono-Regular, monospace;
  font-size: 28px; font-weight: 700; color: var(--green);
  letter-spacing: -0.01em;
}
.lv2 .proof-tile .num .pct { color: var(--text-muted); font-size: 16px; font-weight: 500; }
.lv2 .proof-tile .lbl {
  margin-top: 4px;
  font-family: 'JetBrains Mono', ui-monospace, SFMono-Regular, monospace;
  font-size: 10px; letter-spacing: 0.08em; text-transform: uppercase;
  color: var(--text-faint);
}

/* Sections */
.lv2 section.block { padding: 96px 0; border-bottom: 1px solid var(--border-soft); }
.lv2 section.block.alt { background: var(--bg-soft); }
.lv2 .block-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 64px; align-items: center; }
.lv2 .block-grid.reverse > div:first-child { order: 2; }
@media (max-width: 880px) {
  .lv2 .block-grid, .lv2 .block-grid.reverse { grid-template-columns: 1fr; gap: 40px; }
  .lv2 .block-grid.reverse > div:first-child { order: 0; }
}

/* Features */
.lv2 .features {
  display: grid; grid-template-columns: repeat(3, 1fr); gap: 28px;
  margin-top: 48px;
}
@media (max-width: 880px) { .lv2 .features { grid-template-columns: 1fr; } }
.lv2 .feature {
  background: var(--bg-card);
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 28px;
  position: relative;
}
.lv2 .feature .num {
  font-family: 'JetBrains Mono', ui-monospace, SFMono-Regular, monospace;
  font-size: 11px; font-weight: 700;
  letter-spacing: 0.08em; text-transform: uppercase;
  color: var(--signal); margin-bottom: 14px;
}
.lv2 .feature h3 {
  font-size: 20px; font-weight: 700; letter-spacing: -0.015em;
  color: var(--text); margin-bottom: 10px; line-height: 1.2;
}
.lv2 .feature p { font-size: 14.5px; color: var(--text-muted); line-height: 1.65; }
.lv2 .feature .chips {
  margin-top: 14px;
  display: flex; flex-wrap: wrap; gap: 6px;
}
.lv2 .feature .chip {
  font-family: 'JetBrains Mono', ui-monospace, SFMono-Regular, monospace;
  font-size: 9.5px;
  letter-spacing: 0.08em; text-transform: uppercase;
  padding: 4px 8px;
  background: var(--signal-soft); color: var(--signal);
  border: 1px solid var(--signal-line);
  border-radius: 3px;
}

/* Founders */
.lv2 .founders {
  display: grid; grid-template-columns: 1fr 1fr; gap: 32px; margin-top: 48px;
}
@media (max-width: 700px) { .lv2 .founders { grid-template-columns: 1fr; } }
.lv2 .founder {
  display: flex; gap: 20px; align-items: flex-start;
  background: var(--bg-card);
  border: 1px solid var(--border);
  border-radius: 8px; padding: 24px;
}
.lv2 .founder img {
  width: 88px; height: 88px; border-radius: 50%;
  object-fit: cover; flex-shrink: 0;
  border: 1px solid var(--border);
}
.lv2 .founder h4 {
  font-size: 17px; font-weight: 700; letter-spacing: -0.01em;
  color: var(--text); margin-bottom: 4px;
}
.lv2 .founder .role {
  font-family: 'JetBrains Mono', ui-monospace, SFMono-Regular, monospace;
  font-size: 10px;
  letter-spacing: 0.08em; text-transform: uppercase;
  color: var(--signal); margin-bottom: 10px;
}
.lv2 .founder p { font-size: 13.5px; color: var(--text-muted); line-height: 1.6; }


/* Final CTA + form */
.lv2 .final {
  padding: 120px 0;
  text-align: center;
  background: var(--bg);
}
.lv2 .final h2 { margin: 0 auto; }
.lv2 .final .lede { margin-left: auto; margin-right: auto; }
.lv2 .final-form {
  display: grid; grid-template-columns: 1fr 1fr; gap: 14px;
  max-width: 540px; margin: 40px auto 0;
}
@media (max-width: 600px) { .lv2 .final-form { grid-template-columns: 1fr; } }
.lv2 .final-form input, .lv2 .final-form select {
  font-family: inherit; font-size: 14px;
  padding: 13px 14px;
  border: 1px solid var(--border); border-radius: 4px;
  background: var(--bg-card); color: var(--text);
  outline: none;
  width: 100%;
}
.lv2 .final-form input:focus, .lv2 .final-form select:focus { border-color: var(--signal); }
.lv2 .final-form .full { grid-column: 1 / -1; }
.lv2 .final-form .cta { width: 100%; justify-content: center; padding: 15px; font-size: 15px; }
.lv2 .final-signin { text-align: center; font-size: 14px; color: var(--text-faint); margin: 18px auto 0; }
.lv2 .final-signin a { color: var(--signal); }
.lv2 .form-error {
  color: var(--red);
  font-size: 13px;
  margin: 0;
  text-align: left;
  padding: 0 4px;
}

/* Success card */
.lv2 .success-card {
  max-width: 560px;
  margin: 0 auto;
  background: var(--bg-card);
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 40px 36px 32px;
  box-shadow: 0 1px 2px rgba(0,0,0,0.04), 0 4px 16px rgba(0,0,0,0.07), 0 16px 48px rgba(0,0,0,0.05);
  text-align: center;
}
.lv2 .success-check {
  width: 52px; height: 52px;
  border-radius: 50%;
  background: rgba(22,163,74,0.1);
  border: 1.5px solid rgba(22,163,74,0.3);
  display: flex; align-items: center; justify-content: center;
  margin: 0 auto 20px;
}
.lv2 .position-badge {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 6px 14px;
  border-radius: 20px;
  background: rgba(32,88,240,0.08);
  border: 1px solid rgba(32,88,240,0.18);
  margin-top: 14px;
}
.lv2 .position-num {
  font-family: 'JetBrains Mono', ui-monospace, SFMono-Regular, monospace;
  font-size: 12px; font-weight: 700;
  color: var(--signal); letter-spacing: 0.04em;
}
.lv2 .position-lbl { font-size: 12px; color: var(--text-muted); letter-spacing: -0.01em; }

.lv2 .next-steps {
  text-align: left;
  margin: 32px 0 24px;
  padding-top: 24px;
  border-top: 1px solid var(--border);
}
.lv2 .next-steps-label {
  font-family: 'JetBrains Mono', ui-monospace, SFMono-Regular, monospace;
  font-size: 11px; letter-spacing: 0.12em; text-transform: uppercase;
  color: var(--text-muted); margin-bottom: 14px;
}
.lv2 .next-steps ol {
  list-style: none; padding: 0; margin: 0;
  display: flex; flex-direction: column; gap: 12px;
}
.lv2 .next-steps li {
  display: flex; gap: 12px; align-items: flex-start;
  font-size: 14px; color: var(--text-muted); line-height: 1.5;
}
.lv2 .next-steps .step-num {
  font-family: 'JetBrains Mono', ui-monospace, SFMono-Regular, monospace;
  font-size: 10px; font-weight: 700;
  color: var(--signal); letter-spacing: 0.08em;
  flex-shrink: 0; margin-top: 2px; opacity: 0.8;
  min-width: 18px;
}
@media (max-width: 600px) {
  .lv2 .success-card { padding: 28px 22px 24px; }
}

/* Footer */
.lv2 footer {
  padding: 40px 0;
  border-top: 1px solid var(--border);
  background: var(--bg-soft);
}
.lv2 .footer-inner {
  display: flex; justify-content: space-between; align-items: center; gap: 24px;
  flex-wrap: wrap;
  font-size: 12px;
  color: var(--text-faint);
}
.lv2 .footer-inner a { color: var(--text-faint); margin-right: 18px; }
.lv2 .footer-inner a:hover { color: var(--text); }
.lv2 .footer-inner nav { display: flex; flex-wrap: wrap; }

/* ── Mobile responsive — ≤640px ─────────────────────────────────── */
@media (max-width: 640px) {
  .lv2 .container { padding: 0 20px; }
  .lv2 .header-inner { padding: 0 16px; height: 56px; }
  .lv2 .signin-prefix { display: none; }   /* drop "Already invited?" on mobile, keep "Sign in →" visible */
  .lv2 .signin { font-size: 14px; padding: 8px 4px; min-height: 44px; display: inline-flex; align-items: center; }
  .lv2 .header-right { gap: 8px; }
  .lv2 .cta { padding: 10px 16px; font-size: 13.5px; white-space: nowrap; }
  .lv2 .hero { padding: 40px 0 36px; }
  .lv2 section.block { padding: 56px 0; }
  .lv2 .final { padding: 64px 0; }
}
`
